import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { ScrollView } from 'react-native';
import { formatWithMask, Masks } from 'react-native-mask-input';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Divider } from '@/components/ui/divider';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { useColors } from '@/hooks/useColors';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import { isPlaceholderEmail } from '@/util/smartLaunch';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-screen-md' });

/**
 * The reader's contact details, read only. Edit opens its own screen, so the header's Edit
 * button morphs into that screen's Save as it pushes, rather than swapping in place.
 */
export default function ContactDetails() {
  const router = useRouter();

  const userId = useAtomValue(userIdAtom);

  const { data, isLoading } = useQuery(getCurrentUserV2QueryOptions(userId));

  const { bottom } = useSafeAreaInsets();

  const { colors } = useColors();

  const email = isPlaceholderEmail(data?.email) ? '' : data?.email || '';

  const mobilePhone = data?.mobilePhone?.replace('+1', '') || '';

  const rows = [
    { label: 'Primary email', value: email },
    { label: 'Notification email', value: data?.notificationEmail || email || 'Not set' },
    {
      label: 'Mobile number',
      value: formatWithMask({ text: mobilePhone, mask: Masks.USA_PHONE }).masked,
    },
  ];

  return (
    <VStack className={twJoin('flex-1', SCREEN_COLUMN.frame)} style={{ paddingBottom: bottom }}>
      <Header
        contentScrollsUnderHeader
        statusBarBackgroundColor={colors.background[0]}
        title="Contact Details"
        isBackButton
        backRoute={ROUTES.ACCOUNT}
        rightItems={[
          headerItem.edit('Edit contact details', () =>
            router.push(ROUTES.ACCOUNT_CONTACT_DETAILS_EDIT)
          ),
        ]}
      />

      <VStack className="flex-1">
        {isLoading ? (
          <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0" />
        ) : (
          <ScrollView
            contentInsetAdjustmentBehavior="automatic"
            className="w-full flex-1"
            contentContainerClassName={twJoin('gap-4 py-6', SCREEN_COLUMN.content)}>
            {rows.map((row) => (
              <VStack key={row.label} className="gap-4">
                <Text className="font-medium text-typography-900">{row.label}</Text>

                <Text>{row.value}</Text>

                <Divider />
              </VStack>
            ))}
          </ScrollView>
        )}
      </VStack>
    </VStack>
  );
}
